import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "./Button.scss";
import classNames from "classnames";
import { isAbsoluteUrl } from "app/utils/urlDataExtractor";
import { FormattedMessage } from "react-intl";

export const BUTTON_VARIANTS = {
	FILLED: "filled",
	STROKE: "stroke",
	WHITE: "white",
	BORDERLESS: "borderless",
	LINK: "link",
};

export const BUTTON_SIZES = {
	LARGE: "large",
	DEFAULT: "default",
	SMALL: "small",
};

export const BUTTON_ICON_ALIGNMENTS = {
	LEFT: "left",
	RIGHT: "right",
};

const Button = ({
	variant = BUTTON_VARIANTS.FILLED,
	size = BUTTON_SIZES.DEFAULT,
	className = "",
	children,
	label,
	icon,
	alignIcon = BUTTON_ICON_ALIGNMENTS.LEFT,
	disabled = false,
	loading = false,
	rounded = false,
	to,
	submit = false,
	...otherProps
}) => {
	const hasText = children || label;
	const isIconOnly = icon && !hasText;

	const buttonClasses = classNames(
		"button",
		`button--${size}`,
		`button--${variant}`,
		{
			"button--rounded": rounded,
			"button--icon-only": isIconOnly,
		},
		className
	);

	const content = loading ? (
		<span className="button__label">
			<FormattedMessage id="general.button.loading.label" />
		</span>
	) : (
		<>
			{icon && !isIconOnly && alignIcon === BUTTON_ICON_ALIGNMENTS.LEFT && (
				<span className="button__icon button__icon--left">{icon}</span>
			)}
			{icon && isIconOnly && <span className="button__icon">{icon}</span>}
			{hasText && !rounded && <span className="button__label">{children || label}</span>}
			{icon && !isIconOnly && alignIcon === BUTTON_ICON_ALIGNMENTS.RIGHT && (
				<span className="button__icon button__icon--right">{icon}</span>
			)}
		</>
	);

	if (to) {
		if (isAbsoluteUrl(to) || isAbsoluteUrl(to?.pathname)) {
			return (
				<a
					href={to?.pathname || to}
					className={buttonClasses}
					aria-disabled={disabled || loading ? "true" : undefined}
					role="button"
					{...otherProps}
				>
					{content}
				</a>
			);
		}

		return (
			<Link
				to={to}
				className={buttonClasses}
				aria-disabled={disabled || loading ? "true" : undefined}
				role="button"
				{...otherProps}
			>
				{content}
			</Link>
		);
	}

	return (
		// eslint-disable-next-line react/button-has-type
		<button
			type={submit ? "submit" : "button"}
			className={buttonClasses}
			disabled={disabled || loading}
			aria-disabled={disabled || loading ? "true" : undefined}
			{...otherProps}
		>
			{content}
		</button>
	);
};

Button.propTypes = {
	variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
	size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
	className: PropTypes.string,
	children: PropTypes.node,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	icon: PropTypes.node,
	alignIcon: PropTypes.oneOf(Object.values(BUTTON_ICON_ALIGNMENTS)),
	loading: PropTypes.bool,
	disabled: PropTypes.bool,
	rounded: PropTypes.bool,
	submit: PropTypes.bool,
	to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default Button;
